import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./FilterNearbyIncidentsMenu.module.css";

import { Select } from "shared/Select/Select";
import Button from "shared/Button";
import SearchBar from "shared/SearchBar";
import { DateRangePicker } from "shared/DateRangePicker/DateRangePicker";
import Card from "shared/Card";
import { ChevronDown } from "react-ikonate";

/**
 * Interactive component.
 * Filters the list of nearby incidents on the Event Details page.
 */
export function FilterNearbyIncidentsMenu({ onClick }) {
  const [isDisplayDatePicker, setIsDisplayDatePicker] = useState(false);

  const [filters, setFilters] = useState({
    status: "Select",
    type: "Select",
    severity: "Select",
    caseOwner: "Select",
    search: null,
  });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Closes DateRangePicker when not targeted.
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  const dateButtonRef = useRef();

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setIsDisplayDatePicker(false);
        } else if (
          event.target === dateButtonRef.current &&
          !isDisplayDatePicker
        )
          // Handles opening dropdown
          setIsDisplayDatePicker(true);
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const cardChildren = (
    <div className={styles.cardContainer}>
      {[
        {
          title: "Status",
          options: ["New", "Read", "Reviewed", "Action Required", "Closed"],
        },
        {
          title: "Type",
          options: [
            "Harassment",
            "Hate Speech",
            "Violence",
            "Lookup Abuse",
            "Other",
          ],
        },
        {
          title: "Severity",
          options: ["Level 1", "Level 2", "Level 3", "Level 4", "Level 5"],
        },
        {
          title: "Owner",
          options: ["Jack", "Jill", "Diane", "Dale", "Unassigned"],
          property: "caseOwner",
        },
      ].map(({ title, options, property }, index) => (
        <div key={index}>
          <div className={styles.title}>{title}</div>
          <div className={styles.select}>
            <Select
              placeholder={filters[property ?? title.toLowerCase()]}
              options={[...options, "(Reset)"]}
              onChange={(value) =>
                setFilters({
                  ...filters,
                  [property ?? title.toLowerCase()]:
                    value === "(Reset)" ? "Select" : value,
                })
              }
            />
          </div>
        </div>
      ))}
      <div>
        <div className={styles.title}>Date Range</div>
        <div className={styles.container}>
          <button
            className={styles.selectDropdown}
            type="button"
            ref={dateButtonRef}
          >
            <span>
              {!startDate && !endDate
                ? "Select"
                : startDate.toLocaleDateString() +
                  " - " +
                  endDate.toLocaleDateString()}
            </span>
            <span className={styles.selectChevron}>
              <ChevronDown fontSize="1.4em" />
            </span>
          </button>
          {isDisplayDatePicker && (
            <div className={styles["date-picker-container"]} ref={wrapperRef}>
              <DateRangePicker
                startDateString={startDate}
                endDateString={endDate}
                setStartDate={(_date) =>
                  setStartDate(_date ? new Date(_date) : null)
                }
                setEndDate={(_date) =>
                  setEndDate(_date ? new Date(_date) : null)
                }
                allowPastDate={true}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.searchContainer}>
        <div className={styles.title}>Search</div>
        <div>
          <SearchBar
            placeholderText={"(Search by...)"}
            onInput={(value) => setFilters({ ...filters, search: value })}
          />
        </div>
      </div>
      <Button
        children={"UPDATE"}
        className={styles.updateButton}
        onClick={() => onClick({ ...filters, start: startDate, end: endDate })}
      />
    </div>
  );
  return <Card children={cardChildren} className={styles.card} />;
}

FilterNearbyIncidentsMenu.propTypes = {
  /**
   * Function called to filter the incidents when the update button is clicked.
   */
  onClick: PropTypes.func,
};
